import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { API_URL } from '../config';

interface SeasonYearContextType {
  seasonYear: number;
  defaultSeasonYear: number;
  setSeasonYear: (year: number) => void;
  refreshDefaultYear: () => Promise<void>;
  isLoading: boolean;
}

const SeasonYearContext = createContext<SeasonYearContextType | undefined>(undefined);

const SEASON_YEAR_STORAGE_KEY = 'dirtcar_selected_season_year';

export const SeasonYearProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [defaultSeasonYear, setDefaultSeasonYear] = useState<number>(new Date().getFullYear());
  const [seasonYear, setSeasonYearState] = useState<number>(() => {
    const saved = localStorage.getItem(SEASON_YEAR_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : new Date().getFullYear();
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setSeasonYear = (year: number) => {
    setSeasonYearState(year);
    localStorage.setItem(SEASON_YEAR_STORAGE_KEY, String(year));
  };

  const fetchSeasonYear = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/config/season-year`);
      if (response.ok) {
        const data = await response.json();
        setDefaultSeasonYear(data.seasonYear);
        return data.seasonYear;
      }
    } catch (error) {
      console.error('Failed to fetch season year, using default:', error);
    }
    return null;
  }, []);

  const refreshDefaultYear = useCallback(async () => {
    const year = await fetchSeasonYear();
    if (year !== null) {
      // Also update the active selection and localStorage
      setSeasonYearState(year);
      localStorage.setItem(SEASON_YEAR_STORAGE_KEY, String(year));
    }
  }, [fetchSeasonYear]);

  useEffect(() => {
    const init = async () => {
      const year = await fetchSeasonYear();
      if (year !== null) {
        const saved = localStorage.getItem(SEASON_YEAR_STORAGE_KEY);
        if (!saved) {
          setSeasonYearState(year);
        }
      }
      setIsLoading(false);
    };
    init();
  }, [fetchSeasonYear]);

  return (
    <SeasonYearContext.Provider value={{ seasonYear, defaultSeasonYear, setSeasonYear, refreshDefaultYear, isLoading }}>
      {children}
    </SeasonYearContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSeasonYear = (): number => {
  const context = useContext(SeasonYearContext);
  if (context === undefined) {
    throw new Error('useSeasonYear must be used within a SeasonYearProvider');
  }
  return context.seasonYear;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSeasonYearContext = (): SeasonYearContextType => {
  const context = useContext(SeasonYearContext);
  if (context === undefined) {
    throw new Error('useSeasonYearContext must be used within a SeasonYearProvider');
  }
  return context;
};
